import axios from 'axios'
import { captureApiError } from '../lib/sentry'

const BASE_URL = (import.meta.env.VITE_API_URL || '') + '/api/v1'

export const api = axios.create({
  baseURL: BASE_URL,
  // Cookie refresh token (HttpOnly) ikut ke /auth/*. Satu origin dengan API di
  // produksi dan lewat proxy Vite saat pengembangan, jadi tidak ada CORS.
  withCredentials: true,
})

const STORAGE_KEYS = {
  ACCESS: 'osct.accessToken',
  // Kunci LAMA: sebelum refresh token pindah ke cookie HttpOnly. Hanya dibaca
  // sekali untuk memindahkan sesi yang sedang berjalan, lalu dibuang.
  LEGACY_REFRESH: 'osct.refreshToken',
  USER: 'osct.user',
  PROGRAM: 'osct.currentProgramId',
  CLASS: 'osct.currentClassId',
}

// Refresh token tidak lagi disentuh JavaScript: server memasangnya sebagai
// cookie HttpOnly, sehingga celah XSS tidak bisa mencurinya.
export function setTokens({ accessToken }) {
  if (accessToken) localStorage.setItem(STORAGE_KEYS.ACCESS, accessToken)
}

export function clearTokens() {
  localStorage.removeItem(STORAGE_KEYS.ACCESS)
  localStorage.removeItem(STORAGE_KEYS.LEGACY_REFRESH)
  localStorage.removeItem(STORAGE_KEYS.USER)
  localStorage.removeItem(STORAGE_KEYS.PROGRAM)
  localStorage.removeItem(STORAGE_KEYS.CLASS)
}

export function getAccessToken() {
  return localStorage.getItem(STORAGE_KEYS.ACCESS)
}

// Hanya untuk sesi lama yang belum dipindahkan ke cookie (lihat setTokens).
export function getLegacyRefreshToken() {
  return localStorage.getItem(STORAGE_KEYS.LEGACY_REFRESH)
}

// Penanda bahwa ada sesi yang layak diperpanjang. Cookie HttpOnly tidak bisa
// ditanyakan dari sini, jadi yang dipakai adalah ada-tidaknya access token.
export function hasSession() {
  return Boolean(localStorage.getItem(STORAGE_KEYS.ACCESS))
}

export function setStoredUser(user) {
  if (user) localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user))
  else localStorage.removeItem(STORAGE_KEYS.USER)
}

export function getStoredUser() {
  const raw = localStorage.getItem(STORAGE_KEYS.USER)
  return raw ? JSON.parse(raw) : null
}

export function setStoredProgramId(id) {
  if (id) localStorage.setItem(STORAGE_KEYS.PROGRAM, id)
  else localStorage.removeItem(STORAGE_KEYS.PROGRAM)
}

export function getStoredProgramId() {
  return localStorage.getItem(STORAGE_KEYS.PROGRAM)
}

// Kelas yang sedang dibuka. Disimpan terpisah dari program, tapi selalu ikut
// dibuang begitu programnya berganti — kelas milik program lain tidak boleh
// tertinggal sebagai pilihan.
export function setStoredClassId(id) {
  if (id) localStorage.setItem(STORAGE_KEYS.CLASS, id)
  else localStorage.removeItem(STORAGE_KEYS.CLASS)
}

export function getStoredClassId() {
  return localStorage.getItem(STORAGE_KEYS.CLASS)
}

// --- interceptors ------------------------------------------------
api.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

let refreshing = null

async function refreshAccessToken() {
  if (refreshing) return refreshing
  // Sesi yang dibuat sebelum token pindah ke cookie masih menyimpannya di
  // localStorage. Dikirim sekali agar server memasangkan cookie-nya, lalu dibuang.
  const legacy = localStorage.getItem(STORAGE_KEYS.LEGACY_REFRESH)
  refreshing = axios
    .post(`${BASE_URL}/auth/refresh`, legacy ? { refreshToken: legacy } : {}, { withCredentials: true })
    .then((r) => {
      setTokens({ accessToken: r.data.accessToken })
      localStorage.removeItem(STORAGE_KEYS.LEGACY_REFRESH)
      return r.data.accessToken
    })
    .finally(() => {
      refreshing = null
    })
  return refreshing
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    // Permintaan yang dibatalkan pengguna (mis. tombol Batal pada unggahan)
    // bukan galat: tanpa ini ia terbaca sebagai kegagalan jaringan dan masuk Sentry.
    if (axios.isCancel(error)) return Promise.reject(error)
    const original = error.config
    if (error.response?.status === 401 && !original._retry && hasSession()) {
      original._retry = true
      try {
        const newToken = await refreshAccessToken()
        original.headers.Authorization = `Bearer ${newToken}`
        return api(original)
      } catch (e) {
        clearTokens()
        // Force back to login
        window.dispatchEvent(new Event('osct:auth-expired'))
        return Promise.reject(e)
      }
    }
    // Staf wajib 2FA yang belum mendaftar memegang token terbatas: server menolak
    // semua rute lain dengan kode ini, dan layar pendaftaran yang harus terbuka.
    if (error.response?.status === 403 && error.response?.data?.code === 'MFA_SETUP_REQUIRED') {
      window.dispatchEvent(new Event('osct:mfa-setup-required'))
      return Promise.reject(error)
    }
    // Report genuine server faults / network errors to Sentry (no-op when
    // disabled; ignores expected 4xx internally).
    captureApiError(error)
    return Promise.reject(error)
  }
)
